/**
 * ElevenLabsNarrationPanel — Admin control panel for the ElevenLabs
 * narrator management system.
 *
 * Three sub-tabs:
 *   1. API & Pricing  — key status + per-book price
 *   2. Voice Library  — live ElevenLabs voice list with preview
 *   3. Actor Profiles — create / edit narrator personas
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mic, Play, Square, Loader2, Plus, Trash2, Edit2, Check,
  X, RefreshCw, Headphones, Video, Image, DollarSign,
  ChevronDown, ChevronUp, User, Zap, ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

// ─── tiny helpers ────────────────────────────────────────────
const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
        active
          ? 'bg-background border border-border shadow-sm text-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ─── Voice preview audio hook ────────────────────────────────
function useAudioPreview() {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingId(null);
  }, []);

  const play = useCallback(async (id, url) => {
    if (playingId === id) { stop(); return; }
    stop();
    if (!url) { toast.error('No preview URL for this voice.'); return; }
    setLoadingId(id);
    try {
      const audio = new Audio(url);
      audio.crossOrigin = 'anonymous';
      audio.onended = () => { setPlayingId(null); audioRef.current = null; };
      audio.onerror = () => { setPlayingId(null); setLoadingId(null); audioRef.current = null; };
      audioRef.current = audio;
      await audio.play();
      setPlayingId(id);
    } catch {
      toast.error('Could not play preview.');
    } finally {
      setLoadingId(null);
    }
  }, [playingId, stop]);

  useEffect(() => () => stop(), [stop]);
  return { playingId, loadingId, play, stop };
}

// ════════════════════════════════════════════════════════════
//  Sub-tab 1 — API & Pricing
// ════════════════════════════════════════════════════════════
function ApiConfigTab() {
  const [config, setConfig] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await http.get('/admin/narration/config');
      setConfig(r.data);
      setPriceInput((r.data.price_per_book_cents / 100).toFixed(2));
    } catch {
      toast.error('Could not load config.');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const standardCents = Math.round(parseFloat(document.getElementById('standard-price-input')?.value || '9.99') * 100);
    const studioCents   = Math.round(parseFloat(document.getElementById('studio-price-input')?.value   || '24.99') * 100);
    if (isNaN(standardCents) || isNaN(studioCents)) { toast.error('Enter valid prices.'); return; }
    setSaving(true);
    try {
      const r = await http.put('/admin/narration/config', {
        standard_price_cents: standardCents,
        studio_price_cents:   studioCents,
      });
      setConfig(r.data);
      toast.success('Pricing updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-lg space-y-5">
      {/* Key status */}
      <div className="rounded-lg border border-border p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-[13px] font-semibold">ElevenLabs API Key</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {config.api_key_configured
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11.5px] text-emerald-700"><Check className="h-3 w-3" /> Connected</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[11.5px] text-rose-700"><X className="h-3 w-3" /> Not configured</span>
          }
          <span className="text-[12px] text-muted-foreground font-mono">{config.api_key_preview}</span>
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Set <code className="rounded bg-muted px-1 text-[11px]">ELEVENLABS_API_KEY</code> in your environment to connect.
        </p>
      </div>

      {/* Per-book price — two tiers */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-primary" />
          <span className="text-[13px] font-semibold">Per-Book Narration Pricing</span>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Standard tier uses Flash v2.5 ($0.04/1K chars). Studio tier uses Multilingual v2 ($0.08/1K chars).
        </p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Standard (Flash v2.5)', key: 'standard_price_cents', field: 'standardPrice', testid: 'standard-price-input' },
            { label: 'Studio (Multilingual v2)', key: 'studio_price_cents', field: 'studioPrice', testid: 'studio-price-input' },
          ].map(({ label, key, testid }) => (
            <div key={key} className="space-y-1">
              <label className="text-[11.5px] font-semibold text-muted-foreground">{label}</label>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">$</span>
                <input
                  type="number" min="0" step="0.01"
                  defaultValue={key === 'standard_price_cents'
                    ? (config.standard_price_cents / 100).toFixed(2)
                    : (config.studio_price_cents / 100).toFixed(2)}
                  id={testid}
                  className="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
                  data-testid={testid}
                />
              </div>
            </div>
          ))}
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="narration-price-save-btn" className="gap-1 mt-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save pricing
        </Button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  Sub-tab 2 — Voice Library
// ════════════════════════════════════════════════════════════
function VoiceLibraryTab({ onAssign }) {
  const [voices, setVoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { playingId, loadingId, play } = useAudioPreview();

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/narration/voices');
      setVoices(r.data.voices || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch voices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = (voices || []).filter((v) =>
    !search || v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.labels?.gender || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.labels?.accent || '').toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Search voices…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-xs rounded-md border border-border bg-background px-3 py-1.5 text-[13px] outline-none focus:border-primary"
          data-testid="voice-search-input"
        />
        <Button size="sm" variant="outline" onClick={load} className="gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
        <span className="text-[12px] text-muted-foreground">{filtered.length} voices</span>
      </div>

      <div className="max-h-[480px] overflow-y-auto space-y-2 pr-1">
        {filtered.map((v) => (
          <div
            key={v.voice_id}
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 hover:border-primary/40 transition-colors"
            data-testid={`voice-row-${v.voice_id}`}
          >
            <button
              type="button"
              onClick={() => play(v.voice_id, v.preview_url)}
              disabled={loadingId === v.voice_id}
              className="flex-shrink-0 rounded-full bg-primary/10 p-1.5 hover:bg-primary/20 transition-colors"
              data-testid={`voice-play-${v.voice_id}`}
            >
              {loadingId === v.voice_id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                : playingId === v.voice_id
                  ? <Square className="h-3.5 w-3.5 text-primary" />
                  : <Play className="h-3.5 w-3.5 text-primary" />}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold truncate">{v.name}</span>
                {v.category && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10.5px] text-muted-foreground capitalize">
                    {v.category}
                  </span>
                )}
                {v.labels?.gender && (
                  <span className="rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10.5px] text-sky-700 dark:text-sky-300 capitalize">
                    {v.labels.gender}
                  </span>
                )}
                {v.labels?.accent && (
                  <span className="rounded-full bg-violet-500/10 px-1.5 py-0.5 text-[10.5px] text-violet-700 dark:text-violet-300 capitalize">
                    {v.labels.accent}
                  </span>
                )}
              </div>
              {v.description && (
                <p className="text-[11.5px] text-muted-foreground truncate mt-0.5">{v.description}</p>
              )}
            </div>

            <Button
              size="sm" variant="outline"
              onClick={() => onAssign(v)}
              className="flex-shrink-0 gap-1 text-[12px]"
              data-testid={`voice-assign-${v.voice_id}`}
            >
              <Plus className="h-3.5 w-3.5" /> Assign to actor
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  Actor form (used for create + edit)
// ════════════════════════════════════════════════════════════
function ActorForm({ initial, assignedVoice, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    name: '',
    bio: '',
    voice_id: '',
    voice_name: '',
    portrait_url: '',
    video_url: '',
    sample_text: 'The morning light filtered through the curtains, casting long shadows across the wooden floor. She had waited for this moment her entire life.',
    is_active: true,
    sort_order: 0,
    ...(initial || {}),
    ...(assignedVoice ? { voice_id: assignedVoice.voice_id, voice_name: assignedVoice.name } : {}),
  }));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Narrator Name *</label>
          <input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Sarah Mitchell"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
            data-testid="actor-form-name"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">ElevenLabs Voice ID *</label>
          <input
            value={form.voice_id}
            onChange={(e) => set('voice_id', e.target.value)}
            placeholder="21m00Tcm4Tlv…"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px] font-mono"
            data-testid="actor-form-voice-id"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Narration Tier</label>
          <div className="flex gap-2 mt-1">
            {[
              { value: 'standard', label: 'Standard', desc: 'Flash v2.5 — $9.99/book' },
              { value: 'studio',   label: 'Studio',   desc: 'Multilingual v2 — $24.99/book' },
            ].map(t => (
              <button key={t.value} type="button"
                onClick={() => set('narration_tier', t.value)}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 text-left transition-colors',
                  form.narration_tier === t.value
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background hover:border-primary/40',
                )}
                data-testid={`actor-tier-${t.value}`}
              >
                <p className="text-[12px] font-semibold">{t.label}</p>
                <p className="text-[10.5px] text-muted-foreground">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Short Bio</label>
        <textarea
          value={form.bio}
          onChange={(e) => set('bio', e.target.value)}
          rows={2}
          placeholder="e.g. Professional voice actress specializing in romance and literary fiction…"
          className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
          data-testid="actor-form-bio"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Image className="inline h-3 w-3 mr-0.5" /> Portrait Image URL
          </label>
          <input
            value={form.portrait_url}
            onChange={(e) => set('portrait_url', e.target.value)}
            placeholder="https://…/portrait.jpg"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
            data-testid="actor-form-portrait-url"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Video className="inline h-3 w-3 mr-0.5" /> Actor Video URL
          </label>
          <input
            value={form.video_url}
            onChange={(e) => set('video_url', e.target.value)}
            placeholder="https://…/actor-reel.mp4 or YouTube"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
            data-testid="actor-form-video-url"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Sample Text (used for voice preview generation)
        </label>
        <textarea
          value={form.sample_text}
          onChange={(e) => set('sample_text', e.target.value)}
          rows={2}
          className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
          data-testid="actor-form-sample-text"
        />
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            max="100"
            value={form.sort_order}
            onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)}
            className="w-16 rounded-md border border-border bg-background px-2 py-1 text-[13px] text-center"
            data-testid="actor-form-sort-order"
          />
          <label className="text-[12px] text-muted-foreground">Sort order</label>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            data-testid="actor-form-is-active"
          />
          <span className="text-[12px] text-muted-foreground">Active (visible to writers)</span>
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={() => onSave(form)} disabled={saving || !form.name || !form.voice_id} data-testid="actor-form-save-btn" className="gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {initial ? 'Save changes' : 'Create actor'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  Sub-tab 3 — Actor Profiles
// ════════════════════════════════════════════════════════════
function ActorProfilesTab({ preAssignVoice, onVoiceAssignConsumed }) {
  const [actors, setActors] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sampleLoadingId, setSampleLoadingId] = useState(null);
  const { playingId, loadingId: audioLoadingId, play, stop } = useAudioPreview();

  const [sampleAudios, setSampleAudios] = useState({});

  const load = async () => {
    try {
      const r = await http.get('/admin/narration/actors');
      setActors(r.data.actors || []);
    } catch {
      toast.error('Could not load actors.');
    }
  };

  useEffect(() => { load(); }, []);

  // Open the create form pre-filled with a voice from the Voice Library tab
  useEffect(() => {
    if (preAssignVoice) {
      setCreating(true);
      onVoiceAssignConsumed?.();
    }
  }, [preAssignVoice, onVoiceAssignConsumed]);

  const createActor = async (form) => {
    setSaving(true);
    try {
      await http.post('/admin/narration/actors', form);
      toast.success('Actor created.');
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed.');
    } finally {
      setSaving(false);
    }
  };

  const updateActor = async (aid, form) => {
    setSaving(true);
    try {
      await http.put(`/admin/narration/actors/${aid}`, form);
      toast.success('Actor updated.');
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const deleteActor = async (aid) => {
    setDeletingId(aid);
    try {
      await http.delete(`/admin/narration/actors/${aid}`);
      toast.success('Actor deleted.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const generateSample = async (aid) => {
    setSampleLoadingId(aid);
    try {
      const r = await http.post(`/admin/narration/actors/${aid}/sample-audio`);
      const url = r.data.audio_data_url;
      setSampleAudios((p) => ({ ...p, [aid]: url }));
      // Auto-play
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sample generation failed.');
    } finally {
      setSampleLoadingId(null);
    }
  };

  if (!actors) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      {!creating && (
        <Button size="sm" onClick={() => setCreating(true)} data-testid="create-actor-btn" className="gap-1">
          <Plus className="h-3.5 w-3.5" /> New narrator persona
        </Button>
      )}

      {creating && (
        <ActorForm
          initial={null}
          assignedVoice={preAssignVoice}
          onSave={createActor}
          onCancel={() => setCreating(false)}
          saving={saving}
        />
      )}

      {actors.length === 0 && !creating && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
          <User className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
          <p className="text-[13.5px] text-muted-foreground">
            No narrator personas yet. Create one above or assign from the Voice Library tab.
          </p>
        </div>
      )}

      {actors.map((actor) => (
        <div
          key={actor.id}
          className="rounded-lg border border-border bg-card overflow-hidden"
          data-testid={`actor-card-${actor.id}`}
        >
          {editingId === actor.id ? (
            <div className="p-4">
              <ActorForm
                initial={actor}
                assignedVoice={null}
                onSave={(form) => updateActor(actor.id, form)}
                onCancel={() => setEditingId(null)}
                saving={saving}
              />
            </div>
          ) : (
            <div className="flex gap-4 p-4">
              {/* Portrait */}
              <div className="flex-shrink-0">
                {actor.portrait_url ? (
                  <img
                    src={actor.portrait_url}
                    alt={actor.name}
                    className="h-16 w-16 rounded-full object-cover border-2 border-border"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 border-2 border-border">
                    <User className="h-7 w-7 text-primary/60" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] font-semibold">{actor.name}</span>
                  {!actor.is_active && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground">Inactive</span>
                  )}
                  {actor.voice_name && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] text-primary font-medium">
                      <Mic className="inline h-2.5 w-2.5 mr-0.5" />{actor.voice_name}
                    </span>
                  )}
                </div>
                {actor.bio && (
                  <p className="text-[12.5px] text-muted-foreground mt-0.5 line-clamp-2">{actor.bio}</p>
                )}
                <div className="mt-1 flex items-center gap-3 text-[11.5px] text-muted-foreground">
                  <span className="font-mono">{actor.voice_id}</span>
                  {actor.video_url && <span className="text-sky-600"><Video className="inline h-3 w-3 mr-0.5" />Video attached</span>}
                  {actor.portrait_url && <span className="text-emerald-600"><Image className="inline h-3 w-3 mr-0.5" />Portrait attached</span>}
                </div>

                {/* Sample audio controls */}
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => generateSample(actor.id)}
                    disabled={sampleLoadingId === actor.id}
                    className="gap-1 h-7 text-[12px]"
                    data-testid={`actor-gen-sample-${actor.id}`}
                  >
                    {sampleLoadingId === actor.id
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Headphones className="h-3 w-3" />}
                    {sampleLoadingId === actor.id ? 'Generating…' : 'Generate voice sample'}
                  </Button>
                  {sampleAudios[actor.id] && (
                    <audio controls src={sampleAudios[actor.id]} className="h-7 max-w-[200px]" />
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-1 flex-shrink-0">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setEditingId(actor.id)}
                  className="gap-1 h-7 text-[12px]"
                  data-testid={`actor-edit-${actor.id}`}
                >
                  <Edit2 className="h-3 w-3" /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm" variant="outline"
                      disabled={deletingId === actor.id}
                      className="gap-1 h-7 text-[12px] text-rose-600 hover:text-rose-700"
                      data-testid={`actor-delete-${actor.id}`}
                    >
                      {deletingId === actor.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{actor.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This narrator persona will be removed from the platform. Writers who selected this narrator will need to choose another.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteActor(actor.id)} className="bg-rose-600 hover:bg-rose-700">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  Sub-tab 4 — Genre Covers
// ════════════════════════════════════════════════════════════
function GenreCoversTab() {
  const [running,  setRunning]  = useState(false);
  const [results,  setResults]  = useState(null);

  const generate = async () => {
    if (running) return;
    setRunning(true);
    setResults(null);
    try {
      const r = await http.post('/admin/narration/genres/generate-covers');
      setResults(r.data);
      toast.success(`Generated ${r.data.generated}/${r.data.total} genre covers.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cover generation failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-lg space-y-5">
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-primary" />
          <span className="text-[13px] font-semibold">Audiobook Genre Covers</span>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Generate cover tile images for all 9 audiobook genres using Nano Banana AI.
          Each genre's <code className="rounded bg-muted px-1 text-[11px]">image_prompt</code> is
          used as the visual brief. Results are stored and served via the bookstore API.
        </p>
        <Button
          size="sm"
          onClick={generate}
          disabled={running}
          data-testid="generate-genre-covers-btn"
          className="gap-1"
        >
          {running
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
            : <><ImageIcon className="h-3.5 w-3.5" /> Generate all genre covers</>}
        </Button>
      </div>

      {results && (
        <div className="rounded-lg border border-border p-4 space-y-2">
          <p className="text-[12.5px] font-semibold">
            Results — {results.generated}/{results.total} succeeded
          </p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {(results.results || []).map((r) => (
              <div
                key={r.slug}
                className={cn(
                  'flex items-start gap-2 rounded-md px-2.5 py-1.5 text-[12px]',
                  r.ok ? 'bg-emerald-500/8' : 'bg-rose-500/8',
                )}
                data-testid={`genre-cover-result-${r.slug}`}
              >
                {r.ok
                  ? <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  : <X className="h-3.5 w-3.5 text-rose-600 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{r.name || r.slug}</span>
                  {r.ok && r.url && (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 text-primary underline text-[11px]"
                    >
                      View
                    </a>
                  )}
                  {!r.ok && r.error && (
                    <p className="text-rose-600/80 text-[11px] mt-0.5">{r.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
//  Root panel
// ════════════════════════════════════════════════════════════
export default function ElevenLabsNarrationPanel() {
  const [tab, setTab] = useState('actors');
  const [assignVoice, setAssignVoice] = useState(null);

  const handleAssign = (voice) => {
    setAssignVoice(voice);
    setTab('actors');
  };

  return (
    <div className="p-4 space-y-4" data-testid="elevenlabs-narration-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold flex items-center gap-2">
            <Mic className="h-4 w-4 text-primary" /> ElevenLabs Narrator Studio
          </h2>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            Manage narrator personas, browse voices, and set per-book pricing for Loom writers.
          </p>
        </div>
      </div>

      {/* Sub-tab bar */}
      <div className="flex gap-1 rounded-lg bg-muted/40 p-1 w-fit">
        <Tab active={tab === 'actors'} onClick={() => setTab('actors')}>Actor Profiles</Tab>
        <Tab active={tab === 'voices'} onClick={() => setTab('voices')}>Voice Library</Tab>
        <Tab active={tab === 'config'} onClick={() => setTab('config')}>API & Pricing</Tab>
        <Tab active={tab === 'covers'} onClick={() => setTab('covers')}>Genre Covers</Tab>
      </div>

      {tab === 'config'  && <ApiConfigTab />}
      {tab === 'voices'  && <VoiceLibraryTab onAssign={handleAssign} />}
      {tab === 'covers'  && <GenreCoversTab />}
      {tab === 'actors'  && (
        <ActorProfilesTab
          preAssignVoice={assignVoice}
          onVoiceAssignConsumed={() => setAssignVoice(null)}
        />
      )}
    </div>
  );
}
